import type { PillFactory, StackProps, Styles } from "@mantine/core";
import { Group, NumberInput, Stack, Text } from "@mantine/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { ContainerWithState } from "../../../service/containers/useGetContainerStates";
import { StyleableTagsInput } from "../StyleableTagsInput/StyleableTagsInput";

import { colorLookup } from "./colorLookup";
import classes from "./ContainerIdSelection.module.css";
import { ContainerStateLegend } from "./ContainerStateLegend";
import type { useNumberInputRange } from "./useNumberInputRange";

export type Mode = "fill" | "empty";

interface ContainerIdSelectionProps extends StackProps {
  mode: Mode;
  containerStates: ContainerWithState[];
  isPending?: boolean;
  /** Callback invoked ONLY when the list of selected IDs changes due to *manual* user interaction
   * within the `StyleableTagsInput` (adding/removing tags).
   * *Not* called for range input changes or external resets. */
  onManualChange: (newIds: string[]) => void;
  error: string;
  /** The currently selected container IDs (controlled). */
  value: string[];
  /** The current value of the 'From' range input (controlled). */
  numberInputFrom: ReturnType<typeof useNumberInputRange>["numberInputFrom"];
  /** The current value of the 'To' range input (controlled). */
  numberInputTo: ReturnType<typeof useNumberInputRange>["numberInputTo"];
  /** Callback to update the 'From' range input value. */
  setNumberInputFrom: ReturnType<typeof useNumberInputRange>["setNumberInputFrom"];
  /** Callback to update the 'To' range input value. */
  setNumberInputTo: ReturnType<typeof useNumberInputRange>["setNumberInputTo"];
  /** Validation error for the 'From' range input. */
  errorInputFrom: ReturnType<typeof useNumberInputRange>["errorInputFrom"];
  /** Validation error for the 'To' range input. */
  errorInputTo: ReturnType<typeof useNumberInputRange>["errorInputTo"];
  /** If true, disables all interactions with the component. */
  disabled?: boolean;
  /** If true, makes all inputs read-only and prevents modification actions. */
  readOnly?: boolean;
}

/**
 * A component for selecting container IDs using two mutually exclusive methods:
 * 1.  **Manual Tag Input:** Uses `StyleableTagsInput` for entering container names.
 *     - Tags are created by typing a name followed by a space, comma, or the Enter key.
 *     - The last tag can be deleted by pressing Backspace when the input field is empty.
 *     - All tags can be cleared using the clear button within the input field.
 *     - Tags are visually styled based on the `containerStates` prop and the current `mode`.
 *     - A legend explaining the tag colors is shown when tags are present.
 * 2.  **Numerical Range Input:** Uses two `NumberInput` fields ('Von', 'Bis') to define a range.
 *     - Entering a valid numerical range (e.g., 5 to 7) generates tags for all numbers within that range
 *       (inclusive, e.g., "5", "6", "7"). This is handled by the parent `Selection` component via the `idRange`
 *       returned by `useNumberInputRange`.
 *     - Basic validation prevents 'From' > 'To', ranges exceeding a maximum size (currently 100),
 *       and missing 'From' or 'To' values when the other is present. This validation is handled by `useNumberInputRange`.
 *
 * **Interaction Logic (coordinated with parent `Selection` component):**
 * - When the user interacts with the manual input (adds/removes tags via `onManualChange`), the parent component (`Selection`) clears the range inputs (`Von`, `Bis`).
 * - When the user enters values into the range inputs, the parent component (`Selection`) calculates `idRange`. If `idRange` is valid, the parent updates the `value` prop passed to this component, effectively replacing manual tags.
 * - This component disables the manual input (`StyleableTagsInput`) if either `numberInputFrom` or `numberInputTo` has a value.
 * - This component disables the range inputs (`NumberInput`) if the `value` prop (representing selected tags) has items AND both `numberInputFrom` and `numberInputTo` are undefined (meaning manual input is active).
 * - If the range inputs are cleared *after* generating tags (making the range invalid again), the generated tags remain visible (because the `value` prop isn't cleared by this component) and the manual input becomes enabled again.
 * - If the `containerStates` prop transitions from having items to being empty externally (e.g., parent component clears the selection), this component resets the range inputs internally if they were active.
 *
 * **Error Handling:**
 * - Displays validation errors related to the numerical range input directly below the respective field (passed via `errorInputFrom`/`errorInputTo` props, shown only after blur).
 * - Displays external validation errors (passed via the `error` prop, typically related to container states like "already full") below the manual tag input.
 *
 * **Loading State:**
 * - Shows a loading indicator within the manual tag input when `isPending` is true.
 *
 * **Disabled State:**
 * - If `disabled={true}` is passed, all inputs (manual and range) are disabled, and interactions are prevented.
 *
 * **ReadOnly State:**
 * - If `readOnly={true}` is passed, all inputs (manual and range) are marked as read-only, preventing user input and modification actions (like clearing or removing tags).
 */
export function ContainerIdSelection({
  mode,
  containerStates,
  onManualChange,
  error,
  isPending,
  value,
  numberInputFrom,
  numberInputTo,
  setNumberInputFrom,
  setNumberInputTo,
  errorInputFrom,
  errorInputTo,
  disabled = false,
  readOnly = false,
  ...rest
}: ContainerIdSelectionProps) {
  const [numberInputFromTouched, setNumberInputFromTouched] = useState(false);
  const [numberInputToTouched, setNumberInputToTouched] = useState(false);

  const prevContainerStatesLengthRef = useRef<number>(containerStates.length);

  /**
   * Resets the range inputs if the `containerStates` prop
   * transitions from having items to being empty (e.g., context cleared externally)
   * AND the range inputs were previously active.
   */
  useEffect(() => {
    const transitionedToEmpty = containerStates.length === 0 && prevContainerStatesLengthRef.current > 0;
    const rangeWasActive = numberInputFrom !== undefined || numberInputTo !== undefined;

    if (transitionedToEmpty && rangeWasActive) {
      // Batch state updates together to minimize renders
      setNumberInputFrom(undefined);
      setNumberInputTo(undefined);
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Intentional: Resetting touched state when containerStates becomes empty
      setNumberInputFromTouched(false);

      setNumberInputToTouched(false);
    }

    prevContainerStatesLengthRef.current = containerStates.length;
  }, [containerStates.length, numberInputFrom, numberInputTo, setNumberInputFrom, setNumberInputTo]);

  /** Maps container states to styles for the StyleableTagsInput pills. */
  function mapToStyledValues(states: ContainerWithState[]): Map<string, Styles<PillFactory>> {
    return new Map(
      states.map((containerState) => [
        containerState.containerName,
        {
          root: {
            backgroundColor:
              typeof colorLookup[containerState.state] === "string"
                ? (colorLookup[containerState.state] as string)
                : (colorLookup[containerState.state] as Record<Mode, string>)[mode],
          },
        },
      ]),
    );
  }

  /** Handles changes in the 'from' or 'to' range NumberInput fields. */
  const handleRangeInputChange = useCallback(
    (type: "from" | "to", val: string | number) => {
      if (disabled || readOnly) return;

      const numericValue = val === "" ? undefined : Number(val);
      let otherValue: number | undefined;

      if (type === "from") {
        setNumberInputFrom(numericValue);
        setNumberInputFromTouched(true);
        otherValue = numberInputTo;
      } else {
        setNumberInputTo(numericValue);
        setNumberInputToTouched(true);
        otherValue = numberInputFrom;
      }

      // If *both* inputs are now empty/undefined, reset touched state.
      if (numericValue === undefined && otherValue === undefined) {
        setNumberInputFromTouched(false);
        setNumberInputToTouched(false);
        // NOTE: selectedIds (value prop) are NOT cleared here to preserve existing tags if user clears range.
      }
    },
    [disabled, readOnly, numberInputFrom, numberInputTo, setNumberInputFrom, setNumberInputTo],
  );

  /**
   * Handles changes from the manual StyleableTagsInput (tag creation/deletion).
   * Notifies the parent component via `onManualChange`.
   * The parent (`Selection`) is responsible for updating the `value` and clearing range inputs if needed.
   */
  const handleStyleableTagsInputChange = useCallback(
    (newValue: string[]) => {
      if (disabled || readOnly) return;
      // Parent (`Selection`) is responsible for clearing range inputs if needed.
      onManualChange(newValue.map((it) => it.toString()));
    },
    [disabled, readOnly, onManualChange],
  );

  /** Determines if the manual input should be disabled (i.e., range input is active). */
  const disableManualInput = useMemo(
    () => numberInputFrom !== undefined || numberInputTo !== undefined,
    [numberInputFrom, numberInputTo],
  );

  /** Determines if the range inputs should be disabled (i.e., manual input has tags and range is empty). */
  const disableRangeInput = useMemo(
    () => value.length > 0 && numberInputFrom === undefined && numberInputTo === undefined,
    [value.length, numberInputFrom, numberInputTo],
  );

  return (
    <Stack {...rest}>
      <StyleableTagsInput
        classNames={{ inputField: classes.inputField }}
        clearable
        data={[]} // No suggestions data
        data-testid="container-ids-input"
        disabled={disabled || disableManualInput}
        error={error}
        isPending={isPending}
        label="" // Title provided by parent IconPaper
        legend={<ContainerStateLegend mode={mode} />}
        legendOnInput={true}
        mb={20}
        mx={20} // TODO: Check if this margin is correct or should be padding on parent
        onChange={handleStyleableTagsInputChange}
        placeholder="Behälter-Namen eingeben…"
        readOnly={readOnly}
        splitChars={[",", " "]}
        styledValues={mapToStyledValues(containerStates)}
        value={value}
        wrapperProps={{ "data-testid": "container-ids-input-wrapper" }}
      />

      <Stack gap="xs">
        <div style={{ flex: 1, paddingLeft: "var(--mantine-spacing-md)", paddingRight: "var(--mantine-spacing-md)" }}>
          <Text fw={500} size="sm">
            Oder eine Spanne auswählen
          </Text>
          <Text c="dimmed" mt={7} size="xs">
            (Nur Zahlen möglich)
          </Text>
        </div>
        <Group align="flex-start" grow mb={20} mx={20}>
          <NumberInput
            data-testid="container-ids-range-from"
            disabled={disabled || disableRangeInput}
            error={numberInputFromTouched ? errorInputFrom : null}
            label="Von"
            min={1}
            onBlur={() => setNumberInputFromTouched(true)}
            onChange={(val) => handleRangeInputChange("from", val)}
            placeholder="Start-Nummer"
            readOnly={readOnly}
            value={numberInputFrom ?? ""}
          />
          <NumberInput
            data-testid="container-ids-range-to"
            disabled={disabled || disableRangeInput}
            error={numberInputToTouched ? errorInputTo : null}
            label="Bis"
            min={1}
            onBlur={() => setNumberInputToTouched(true)}
            onChange={(val) => handleRangeInputChange("to", val)}
            placeholder="End-Nummer"
            readOnly={readOnly}
            value={numberInputTo ?? ""}
          />
        </Group>
      </Stack>
    </Stack>
  );
}
